import type {
  ComposerUploadHandoff,
  ComposerUploadLifecycle,
} from '@/components/org-chat/composer-upload-lifecycle';
import type { OrgChatAttachmentInput } from '@/components/org-chat/org-chat-types';
import {
  pendingUploadFailureReason,
  type BackgroundUploadJob,
  type BackgroundUploadServer,
} from './background-upload-types';

/** One file the background upload manager owns (#4844). */
export type ManagedUploadJob = BackgroundUploadJob & {
  /** The signed-in member it uploads for. */
  actorId: string;
  lifecycle: ComposerUploadLifecycle;
  reportedBytes: number;
  /** Stored and registered; only the server's `complete` is still owed. */
  stored?: { storageKey: string; workspaceFileId: string };
  /** Resolves once the send this file belongs to exists (false: it never will). */
  bound: Promise<boolean>;
};

/**
 * How a managed job reports to the server: the handoff its lifecycle
 * publishes into, and the `complete` a stored file still owes. A job that is
 * no longer the manager's current one (removed, or its member signed out)
 * reports nothing.
 */
export function createJobReporting(deps: {
  isCurrent: (job: ManagedUploadJob) => boolean;
  server: BackgroundUploadServer;
  forget: (uploadId: string) => void;
  changed: () => void;
  quietly: (step: string, work: Promise<unknown>) => Promise<unknown>;
  report?: (step: string, error: unknown) => void;
}) {
  const { isCurrent, server, forget, changed, quietly } = deps;

  /** Tell the server a stored file is in; a lost call stays retryable. */
  const complete = async (job: ManagedUploadJob) => {
    if (!job.stored || !(await job.bound)) return;
    try {
      await server.complete({
        pendingSendId: job.pendingSendId,
        uploadId: job.uploadId,
        ...job.stored,
      });
      if (isCurrent(job)) forget(job.uploadId);
    } catch (error) {
      deps.report?.('complete', error);
      if (!isCurrent(job)) return;
      job.state = 'failed';
      job.failure = 'network';
      changed();
    }
  };

  const handoffFor = (job: ManagedUploadJob): ComposerUploadHandoff => ({
    publish: (attachment: OrgChatAttachmentInput | null) => {
      if (!isCurrent(job)) return;
      if (!attachment) {
        void quietly(
          'remove',
          job.bound.then(
            (bound) =>
              bound &&
              server.remove({
                pendingSendId: job.pendingSendId,
                uploadId: job.uploadId,
              })
          )
        );
        forget(job.uploadId);
        return;
      }
      if (!attachment.storageKey || !attachment.workspaceFileId) return;
      job.stored = {
        storageKey: attachment.storageKey,
        workspaceFileId: attachment.workspaceFileId,
      };
      job.saving = true;
      changed();
      void complete(job);
    },
    failed: (failure) => {
      if (!isCurrent(job)) return;
      job.state = 'failed';
      job.failure = pendingUploadFailureReason(failure);
      changed();
      const reason = job.failure;
      void quietly(
        'fail',
        job.bound.then(
          (bound) =>
            bound &&
            server.fail({
              pendingSendId: job.pendingSendId,
              uploadId: job.uploadId,
              reason,
            })
        )
      );
    },
  });

  return { handoffFor, complete };
}
